import { z } from 'zod';
import { type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { countries } from './countries';
import type { PhoneChange } from './StanPhoneInput.types';

/** Build quick lookups once, not on every call */
const CODE_TO_DIAL = new Map<string, string>(
    countries.map((c: { code: string; dialCode: string }) => [c.code.toUpperCase(), c.dialCode])
);

const CountryCodeSchema = z
    .string()
    .transform(s => s?.toUpperCase().trim())
    .refine(s => /^[A-Z]{2}$/.test(s), { message: 'countryCode must be ISO 3166-1 alpha-2' })
    .refine(s => CODE_TO_DIAL.has(s), { message: 'Unknown countryCode' });

const RawPhoneSchema = z.string().transform(s => s.trim());

/** Core transformer: raw input + countryCode -> PhoneChange */
const PhoneChangeTransformer = z
    .object({
        raw: RawPhoneSchema,
        countryCode: CountryCodeSchema,
    })
    .transform(({ raw, countryCode }): PhoneChange => {
        // Empty input: treat as "no phone yet" but structurally valid
        if (raw.length === 0) {
            return { e164: '', valid: true, country: countryCode, national: '' };
        }

        // Try parsing with the provided region first
        const parsed = parsePhoneNumberFromString(raw, countryCode as CountryCode);

        if (parsed?.isValid()) {
            return {
                e164: parsed.number,
                valid: true,
                country: parsed.country ?? countryCode,
                national: parsed.formatNational(),
            };
        }

        // Fallback: stitch E.164 using the country dial code + digits only
        const dial = CODE_TO_DIAL.get(countryCode) ?? '';
        const digits = raw.replace(/\D+/g, '');
        const e164 = `${dial.startsWith('+') ? dial : `+${dial}`}${digits}`;

        // Mark invalid so callers can differentiate "formatted but not valid"
        return {
            e164,
            valid: false,
            country: countryCode,
            national: raw,
        };
    });

/** Public API: validation + normalization in one call */
export function parseAndFormatPhone(rawInput: string, countryCode: string): PhoneChange {
    const result = PhoneChangeTransformer.parse({ raw: rawInput, countryCode });
    return result;
}

/** Optional: safe version if you don’t want thrown errors */
export function tryParseAndFormatPhone(rawInput: string, countryCode: string) {
    const result = PhoneChangeTransformer.safeParse({ raw: rawInput, countryCode });
    return result.success ? result.data : null;
}

/** Locale sniffing, but with Zod to avoid weird navigator.language values */
export function getLocaleCountryCode(): string | undefined {
    if (typeof navigator === 'undefined') return undefined;

    // "en-US", "fr", "pt-BR" → try to use the region part if present
    const schema = z
        .string()
        .transform(s => s.split('-')[1] ?? '')
        .transform(s => s.toUpperCase())
        .pipe(CountryCodeSchema.optional());

    const out = schema.safeParse(navigator.language);
    return out.success ? out.data : undefined;
}

/** Detect country directly from any phone-like string */
export function detectCountryFromPhone(phoneInput: string): string | undefined {
    const cleaned = RawPhoneSchema.parse(phoneInput);
    if (!cleaned) return undefined;
    const parsed = parsePhoneNumberFromString(cleaned);
    return parsed?.country ?? undefined;
}
